import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

export const DRAFT_PATHS = {
  lost_ad: () => "/annonces/nouvelle?resume_draft=1",
  found_ad: () => "/annonces/trouvee?resume_draft=1",
  finder_decl: (id) => `/annonces/${id}?resume_draft=1`,
  claim: (id) => `/annonces/${id}?resume_draft=1`,
};
export const draftPath = (key) => {
  const [head, id] = key.split(":");
  return (DRAFT_PATHS[head] || DRAFT_PATHS.lost_ad)(id);
};

const MEANINGFUL = ["description", "circumstances", "lost_place", "found_place", "lost_date", "found_date", "image_base64", "photo", "color", "brand", "message"];
const hasContent = (form) => MEANINGFUL.some((k) => form?.[k] && String(form[k]).trim());
const wantsResume = () => new URLSearchParams(window.location.search).get("resume_draft") === "1";

// Auto-saves `form` server-side (24 h) for logged-in B2C users and restores it on ?resume_draft=1.
export function useDraft(key, form, setForm, { enabled = true, label = "" } = {}) {
  const [ready, setReady] = useState(false);
  const timer = useRef(null);
  const pending = useRef(false);
  const latest = useRef(form);
  latest.current = form;
  const active = enabled && !!key;

  useEffect(() => {
    if (!active) return;
    let gone = false;
    if (!wantsResume()) { setReady(true); return; }
    api(`/b2c/drafts/item?key=${encodeURIComponent(key)}`, { b2c: true })
      .then((d) => { if (!gone && d?.data) setForm((f) => ({ ...f, ...d.data })); })
      .catch(() => {})
      .finally(() => { if (!gone) setReady(true); });
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key]);

  const saveNow = useCallback(async () => {
    clearTimeout(timer.current);
    pending.current = false;
    if (!active || !hasContent(latest.current)) return;
    try {
      await api("/b2c/drafts", { method: "PUT", b2c: true, body: { key, data: latest.current, path: draftPath(key), label } });
    } catch { /* best effort */ }
  }, [active, key, label]);

  useEffect(() => {
    if (!active || !ready || !hasContent(form)) return;
    clearTimeout(timer.current);
    pending.current = true;
    timer.current = setTimeout(saveNow, 1500);
  }, [form, active, ready, saveNow]);

  // Flush an unsaved edit when the form/dialog closes or the page is hidden.
  useEffect(() => {
    if (!active) return;
    const flush = () => { if (pending.current) saveNow(); };
    window.addEventListener("pagehide", flush);
    return () => { window.removeEventListener("pagehide", flush); flush(); };
  }, [active, saveNow]);

  const clear = useCallback(async () => {
    clearTimeout(timer.current);
    pending.current = false;
    if (!active) return;
    try { await api(`/b2c/drafts?key=${encodeURIComponent(key)}`, { method: "DELETE", b2c: true }); } catch { /* ignore */ }
  }, [active, key]);

  return { saveNow, clear, resumePath: draftPath(key || "lost_ad") };
}

const LOCAL_TTL_MS = 24 * 3600 * 1000;

// Browser-side copy of a declaration wizard (form + step) so a reload before signing in loses nothing.
export function useLocalWizardDraft(key, { form, setForm, step, setStep }) {
  const storageKey = `findloop_wizard_${key}`;
  const restored = useRef(false);

  useEffect(() => {
    if (!wantsResume()) {
      try {
        const d = JSON.parse(localStorage.getItem(storageKey) || "null");
        if (d && Date.now() - d.at < LOCAL_TTL_MS && d.form) {
          setForm((f) => ({ ...f, ...d.form }));
          if (d.step) setStep(d.step);
        } else if (d) localStorage.removeItem(storageKey);
      } catch { /* ignore */ }
    }
    restored.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    if (!restored.current || !hasContent(form)) return;
    const id = setTimeout(() => {
      const save = (f) => localStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), step, form: f }));
      try { save(form); } catch {
        try { save({ ...form, image_base64: null, image_base64_2: null }); } catch { /* quota */ }
      }
    }, 500);
    return () => clearTimeout(id);
  }, [form, step, storageKey]);

  const clear = useCallback(() => { try { localStorage.removeItem(storageKey); } catch { /* ignore */ } }, [storageKey]);
  return { clear };
}
